/**
 * Bootstrap.
 *
 * Java ran each persisted class's `static {}` block on first touch. Class-init order is
 * implicit there but load-bearing here (Settings pushes its volume into AudioPlayer), so the
 * ports expose `static init()` and we call them in a fixed order instead — see PORTING_CONTRACT.md.
 */
import { Game } from './Game';
import { STATE } from './STATE';
import { HUD } from './HUD';
import { Settings } from './Settings';
import { Profile } from './Profile';
import { Achievements } from './Achievements';
import { Stats } from './Stats';
import { DailyChallenge } from './DailyChallenge';
import { TrailPool } from './TrailPool';
import { AudioPlayer } from './AudioPlayer';
import { CoinShop } from './CoinShop';
import { Perks } from './Perks';
import { PlayerSkins } from './PlayerSkins';
import { Handler } from './Handler';
import { GameObject } from './GameObject';
import { Player } from './Player';
import { ID } from './ID';
import { EnemyBoss } from './EnemyBoss';
import { BossSplitter } from './BossSplitter';
import { BossLaser } from './BossLaser';
import { BossSwarm } from './BossSwarm';
import { Random } from './java/Random';
import { FileSystem } from './java/DataStream';
import { setQuitHandler } from './quit';
import { unlock as unlockAudio } from './java/Slick';
import { isTouchDevice, shouldAskToRotate, goFullscreenLandscape, setForceTouch } from './Device';

function fail(err: unknown): void {
  const msg = err instanceof Error ? `${err.message}\n${err.stack ?? ''}` : String(err);
  console.error(err);
  const el = document.getElementById('boot-err');
  if (el) el.textContent = msg;
}

function boot(): void {
  const canvas = document.getElementById('game') as HTMLCanvasElement | null;
  if (!canvas) throw new Error('#game canvas not found');
  canvas.tabIndex = 0;

  // ?touch=1 / ?touch=0 forces the control scheme, overriding capability detection. Must run
  // before Game is constructed, which reads it once. Used by the device harness, and a usable
  // escape hatch on any device we mis-detect.
  const params = new URLSearchParams(location.search);
  if (params.has('touch')) setForceTouch(params.get('touch') !== '0');

  const touch = isTouchDevice();
  if (touch) document.body.classList.add('is-touch');

  // Persisted state, in dependency order.
  const hadSettings = FileSystem.exists('dotch_settings.dat');
  Settings.init();
  Profile.init();
  Achievements.init();
  Stats.init();
  DailyChallenge.init();
  TrailPool.init();

  // First run on a phone: dial the effects back. The desktop defaults (High particles, full
  // shake) push a lot of overdraw, and a mid-range phone GPU drops frames in a game where a
  // dropped frame is a death. Only applied when there is no saved config, so a returning
  // player's choices are never overwritten.
  if (touch && !hadSettings) {
    Settings.setParticleDensity(1); // Medium
    Settings.setScreenShake(1);     // Low
    Settings.setShowFps(false);
  }

  const game = new Game(canvas);
  game.start();

  // --- portrait rotate prompt ---
  const rotateEl = document.getElementById('rotate');
  let rotateDismissed = false;
  const syncOrientation = () => {
    const needs = !rotateDismissed && shouldAskToRotate();
    document.body.classList.toggle('needs-rotate', needs);
  };
  document.getElementById('rotate-anyway')?.addEventListener('pointerdown', (ev) => {
    ev.stopPropagation();
    rotateDismissed = true;
    syncOrientation();
  });
  window.addEventListener('resize', syncOrientation);
  window.addEventListener('orientationchange', () => setTimeout(syncOrientation, 150));
  syncOrientation();

  // The menu renders behind the overlay immediately; the click only releases audio,
  // which browsers gate behind a user gesture.
  const bootEl = document.getElementById('boot');
  if (bootEl) {
    // Stays registered so the overlay can be shown again after Quit — hence the guard:
    // without it, every keypress during play would re-enter and restart the music.
    const start = () => {
      if (bootEl.classList.contains('hidden')) return;
      unlockAudio();
      AudioPlayer.play(); // resumes if paused by Quit; starts the track on first boot
      bootEl.classList.add('hidden');
      canvas.focus();
      // Fullscreen + landscape lock need a user gesture, and this tap is the only one we're
      // guaranteed to get. Best-effort: iOS Safari supports neither, which is what the
      // rotate prompt is for.
      if (touch) void goFullscreenLandscape(document.documentElement);
    };
    bootEl.addEventListener('pointerdown', start);
    window.addEventListener('keydown', start);

    // "Quit" can't exit a browser tab (see quit.ts). Halt the game and come back to the
    // boot screen instead, which is at least an honest stop rather than a dead button.
    setQuitHandler(() => {
      AudioPlayer.pause();
      Game.gameState = STATE.Menu;
      const cta = bootEl.querySelector('.cta');
      if (cta) cta.textContent = 'Click to resume';
      bootEl.classList.remove('hidden');
    });
  }

  // Test hook, opt-in via ?debug=1. Lets a harness reach states that would otherwise take
  // an hour of play (level 10 boss, the death/End screen) instead of leaving them unverified.
  // Inert in normal play — nothing is attached to window without the flag.
  if (new URLSearchParams(location.search).has('debug')) {
    const BOSSES = [EnemyBoss, BossSplitter, BossLaser, BossSwarm];
    const BOSS_NAMES = ['EnemyBoss', 'BossSplitter', 'BossLaser', 'BossSwarm'];

    (window as unknown as Record<string, unknown>).__dotch = {
      Game, STATE, HUD, Profile, Achievements, Settings, DailyChallenge, CoinShop,
      Perks, PlayerSkins, AudioPlayer, Random,
      state: () => STATE[Game.gameState],
      setState: (name: keyof typeof STATE) => { Game.gameState = STATE[name]; },
      kill: () => { HUD.HEALTH = 0; },
      setLevel: (n: number) => { Game.getHud()?.setLevel(n); },
      health: () => HUD.HEALTH,
      score: () => Game.getHud()?.getScore() ?? 0,
      level: () => Game.getHud()?.getLevel() ?? 0,
      objects: () => Game.getHandler()?.getObjects().length ?? 0,
      audio: () => ({
        tracks: AudioPlayer.getTrackCount(),
        playing: AudioPlayer.isPlaying(),
        track: AudioPlayer.getCurrentTrack()?.displayName ?? null,
        pos: AudioPlayer.getPosition(),
        beat: AudioPlayer.getBeatPulse(),
      }),
      layout: () => ({
        WIDTH: Game.WIDTH,
        HEIGHT: Game.HEIGHT,
        scale: Game.getScale(),
        canvasW: canvas.width,
        canvasH: canvas.height,
        letterboxed: Game.WIDTH === Game.MIN_WIDTH,
        isTouch: document.body.classList.contains('is-touch'),
        needsRotate: document.body.classList.contains('needs-rotate'),
      }),
      /** Logical -> CSS px, so the harness taps exactly where the game thinks things are. */
      toCss: (lx: number, ly: number) => game.clientFromLogical(lx, ly),
      /** Player movement booleans — lets the harness prove the joystick actually moves you. */
      input: () => {
        const objs = Game.getHandler()?.getObjects() ?? [];
        for (const o of objs) {
          if (o instanceof Player) {
            return { up: o.moveUp, down: o.moveDown, left: o.moveLeft, right: o.moveRight,
                     x: Math.round(o.getX()), y: Math.round(o.getY()) };
          }
        }
        return null;
      },
      // `coins` is private, but only to the compiler — reaching it here keeps the coin-shop
      // purchase path testable without inventing a production-facing mutator.
      grantCoins: (n: number) => { (Profile as unknown as { coins: number }).coins = n; },
      /** Spawn a boss by type index (0=Classic, 1=Splitter, 2=Laser, 3=Swarm). */
      spawnBoss: (type: number) => {
        const h = Game.getHandler();
        if (!h) return false;
        h.clearEnemys();
        const Ctor = BOSSES[type] as new (
          x: number, y: number, id: ID, handler: Handler, gen?: number,
        ) => GameObject;
        const boss = type === 1
          ? new Ctor(Math.trunc(Game.WIDTH / 2) - 48, 120, ID.EnemyBoss, h, 0)
          : new Ctor(Math.trunc(Game.WIDTH / 2) - 48, 120, ID.EnemyBoss, h);
        h.addObject(boss);
        return true;
      },
      bossName: () => {
        const objs = Game.getHandler()?.getObjects() ?? [];
        for (const o of objs) {
          for (let i = 0; i < BOSSES.length; i++) {
            if (o instanceof BOSSES[i]) return BOSS_NAMES[i];
          }
        }
        return null;
      },
    };
  }
}

try {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      try { boot(); } catch (e) { fail(e); }
    });
  } else {
    boot();
  }
} catch (e) {
  fail(e);
}
